import { formatMonthYear } from '../lib/format';
import { LANGUAGES, LANGUAGE_LABELS, useI18n } from '../lib/i18n';
import { useLogout, useOtpPreference, useSession, useUpdateProfile } from '../lib/queries';
import { Button, Card, Skeleton } from '../components/ui';

export function ProfilePage() {
  const { t, language, setLanguage } = useI18n();
  const { data: profile, isPending } = useSession();
  const logout = useLogout();
  const otpPreference = useOtpPreference();
  const updateProfile = useUpdateProfile();

  if (isPending) return <Skeleton className="h-64" />;
  if (!profile) return null;

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold tracking-tight text-ink">{t('profile.title')}</h1>

      <Card>
        <div className="text-lg font-semibold text-ink">{profile.name}</div>
        <div className="tabular mt-1 text-sm text-ink-dim">{profile.phone}</div>
        <div className="tabular mt-0.5 text-xs tracking-[0.14em] text-ink-faint">
          {profile.loyaltyId}
        </div>
        <div className="mt-3 border-t border-line pt-3 text-xs text-ink-faint">
          {t('profile.memberSince')} {formatMonthYear(profile.memberSince, language)}
        </div>
      </Card>

      <Card>
        <div className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
          {t('profile.language')}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {LANGUAGES.map((code) => (
            <button
              key={code}
              onClick={() => {
                setLanguage(code);
                updateProfile.mutate({ preferredLanguage: code });
              }}
              aria-pressed={language === code}
              className={`min-h-11 rounded-xl border text-sm font-medium transition-colors ${
                language === code
                  ? 'border-brand bg-brand-wash text-brand'
                  : 'border-line bg-raised text-ink-dim hover:text-ink'
              }`}
            >
              {LANGUAGE_LABELS[code]}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <div className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
          {t('profile.codes')}
        </div>
        <p className="mt-2 text-[0.85rem] leading-relaxed text-ink-dim">
          {profile.otpOptedOut ? t('profile.codesOff') : t('profile.codesOn')}
        </p>
        <Button
          variant="ghost"
          className="mt-3 w-full"
          loading={otpPreference.isPending}
          onClick={() => otpPreference.mutate(!profile.otpOptedOut)}
        >
          {profile.otpOptedOut ? t('profile.enableCodes') : t('profile.declineCodes')}
        </Button>
      </Card>

      <Button
        variant="quiet"
        className="w-full"
        loading={logout.isPending}
        onClick={() => logout.mutate()}
      >
        {t('profile.logout')}
      </Button>
    </div>
  );
}
